import { DirectLinkFailure } from '#~/electron/direct-link-failure.js';
import {
  createAddFlowDiagnostic,
  safeAddFlowStatus,
  type AddFlowOutcome
} from '#~/agent/components/bridge/add-flow-diagnostic.js';
import {
  directLinkErrorCode,
  logDirectLinkLegFailure
} from '#~/agent/components/bridge/direct-link-log.js';
import {
  createDirectLinkAttemptOwner,
  directLinkAttemptMatches,
  type DirectLinkAttempt,
  type DirectLinkAttemptIdentity
} from '#~/agent/components/bridge/direct-link-attempt.js';
import {
  classifyDirectLinkAuthOutcome,
  DIRECT_LINK_R4_GENERIC_MESSAGE,
  DIRECT_LINK_UNREACHABLE_EVENT_MESSAGE,
  hasDirectLinkIpcInvoke,
  type DirectLinkAuthOperation
} from '#~/agent/components/bridge/direct-link-auth-outcome.js';
import { DIRECT_LINK_TRANSPORT_ERROR_TYPE } from '#~/agent/components/bridge/direct-link-transport-error.js';
import {
  type DirectLinkTimeoutError,
  useConnectToMDNSService,
  useDirectLinkAgent
} from '#~/agent/components/bridge/hooks.js';
import type { DirectLinkRequestDetails } from '#~/agent/components/bridge/screens/search-devices.js';
import {
  ONBOARDING_ADD_METHOD,
  ONBOARDING_PROPERTIES
} from '#~/analytics/constants/onboarding-events.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { resolveDeviceAnalyticsCatalogContext } from '#~/analytics/device-context.js';
import { isEmbeddedDiscovery, resolveEmbeddedFlowProps } from '#~/analytics/domains/index.js';
import { createDeviceLifecycleTracker } from '#~/analytics/domains/device-lifecycle.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { useAuth0 } from '#~/auth/index.js';
import { removeIrisTransitoryCamerasAt } from '#~/device/commands/pending.js';
import { ElectronAPI } from '#~/electron/api.js';
import { isElectronApp } from '#~/electron/utils.js';
import { logger } from '#~/logger/index.js';
import { useAddVideoStore } from '#~/room/components/video/add.js';
import { AddSourceAuth } from '#~/video/components/add/index.js';
import type { Props as AddSourceAuthProps } from '#~/video/components/add/source/types.js';
import type { Api } from '@studio/design-system/dialog';
import { usePrompt } from '@studio/design-system/prompt';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { createElement, forwardRef, useEffect, useRef } from 'react';
// ENG-4649: both imports are small leaves — a zustand store and a
// localStorage wrapper — so the link dialog does not pull the onboarding
// component graph in behind them.
import { clearOnboardingResume, noteEmbeddedLinkForOnboarding } from '#~/onboarding/resume.js';
import { useOnboardingPermissionsClaim } from '#~/onboarding/store.js';
import { useStore } from '../../store/hooks.js';
import { useAddSourceAuthStore } from './credentials-dialog.store.js';

/** Module-scope owner so close/reopen cannot drop an in-flight details→cloud write. */
export const authAttemptOwner = createDirectLinkAttemptOwner();

export { useAddSourceAuthStore };

const AddSourceAuthWrapper = forwardRef<
  Api,
  Omit<AddSourceAuthProps, 'agentIds' | 'appId' | 'devices' | 'discoveries'>
>(({ ...props }, forwardedRef): ReactElement => {
  return createElement(AddSourceAuth, { ...props, ref: forwardedRef });
});

/** True when a protobuf Error actually carries a code or message (not a zero default). */
const hasProtoError = (error: unknown): boolean => {
  const code = directLinkErrorCode(error);
  if (code != null && code !== 0) return true;
  if (!error || typeof error !== 'object' || !('message' in error)) return false;
  const message = (error as { message: unknown }).message;
  return typeof message === 'string' && message.length > 0;
};

const auth0SignInRequired = (error: unknown): boolean => {
  if (!error || typeof error !== 'object' || !('error' in error)) return false;
  const code = (error as { error: unknown }).error;
  return code === 'login_required' || code === 'consent_required' || code === 'invalid_grant';
};

/** `AddSourceAuthModal` component. */
export const AddSourceAuthModal = () => {
  const sourceSelection = useStore((µ) => µ.source.selection);
  const storeAgentId = useStore((µ) => µ.agentId);
  const roomId = useStore((µ) => µ.roomId);
  const {
    show,
    error,
    connecting,
    rawPending,
    lockoutStatus,
    setShow,
    setError,
    setConnecting,
    setRawPending,
    setLockoutStatus
  } = useAddSourceAuthStore();
  const { setShow: setShowVideo } = useAddVideoStore();
  const prompt = usePrompt();
  const { getAccessTokenSilently } = useAuth0();

  const embedded = isEmbeddedDiscovery(sourceSelection);
  // An embedded link dials the camera, so scope it by that endpoint: its Bridge attribution
  // fills in when state-sync discovery catches up and must not supersede the attempt.
  const selectionTarget = embedded
    ? (sourceSelection?.address ?? sourceSelection?.agentId)
    : sourceSelection?.agentId;
  const identity: DirectLinkAttemptIdentity = {
    sourceId: sourceSelection?.id?.toString(),
    agentId: JSON.stringify([selectionTarget, storeAgentId]),
    roomId,
    flow: embedded ? 'embedded' : 'mdns'
  };
  const identityKey = JSON.stringify(identity);
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const lastIdentityKey = useRef(identityKey);
  // Callbacks from a departed modal settle only the attempt it started.
  const attemptRef = useRef<DirectLinkAttempt | null>(null);
  // True only while the "Unlink Bridge" prompt is parked with no cloud write yet in
  // flight, so an unmount before the operator answers can settle the attempt instead
  // of leaking the raw-flight latch for the session.
  // Per settle-every-terminal-branch (ai.knowledge/global).
  const awaitingUnlinkDecisionRef = useRef(false);
  // The agent handed to linkBridgeDirect, so the success callback (which is
  // handed only a boolean) can name it. ENG-4649: the wizard needs it to resume
  // after the reload below.
  const linkedAgentIdRef = useRef<string | undefined>(undefined);
  // The camera this attempt authenticated; discovery can swap the live selection mid-link.
  const attemptSourceRef = useRef<{ name?: string; address?: string }>({});
  // Set when the operator closes the dialog or it unmounts; only then is a link success left unapplied.
  const releasedAttemptRef = useRef<DirectLinkAttempt | null>(null);
  const mountedRef = useRef(false);
  const diagnosticRef = useRef<ReturnType<typeof createAddFlowDiagnostic> | undefined>(undefined);
  const reportAuth = (operation: DirectLinkAuthOperation, reason: string, status?: number) => {
    const categories: Record<string, AddFlowOutcome['category']> = {
      R1: 'refused',
      R2: 'locked',
      R3: 'unreachable',
      R4: 'service',
      R5: 'sign-in',
      R6: 'missing-link',
      R7: 'unsupported',
      R8: 'timeout'
    };
    diagnosticRef.current?.report({
      operation,
      category: categories[reason] ?? 'unknown',
      status,
      retry: reason === 'R8' ? 'check-state' : 'retry'
    });
  };

  const syncPending = () => setRawPending(authAttemptOwner.rawPending());

  const canApplyAttempt = (attempt: DirectLinkAttempt | null): boolean =>
    attempt != null &&
    authAttemptOwner.isCurrent(attempt) &&
    !attempt.presentationTimedOut &&
    directLinkAttemptMatches(attempt, identityRef.current);

  // The cloud already linked this camera: only the operator leaving may skip finishing it.
  // A discovery identity change or display timeout does not undo the link.
  const canFinishLinked = (attempt: DirectLinkAttempt | null): boolean =>
    attempt != null &&
    mountedRef.current &&
    releasedAttemptRef.current !== attempt &&
    authAttemptOwner.current() === attempt;

  const logFinish = (branch: string, attempt: DirectLinkAttempt | null) => {
    logger.info(
      `[direct-link] finish branch=${branch} attempt=${attempt?.id ?? 'none'} current=${
        attempt != null && authAttemptOwner.isCurrent(attempt)
      } identityMatches=${
        attempt != null && directLinkAttemptMatches(attempt, identityRef.current)
      } presentationTimedOut=${attempt?.presentationTimedOut ?? false}`
    );
  };

  const finishAttempt = async (applyUi: boolean, linked: boolean) => {
    const attempt = attemptRef.current;
    const canApply = applyUi && (linked ? canFinishLinked(attempt) : canApplyAttempt(attempt));
    if (!canApply) {
      logFinish(linked ? 'linked-not-applied' : 'failed-not-applied', attempt);
      if (attempt) authAttemptOwner.settle(attempt);
      syncPending();
      return;
    }
    diagnosticRef.current?.report({
      operation: 'cloud-link-agent',
      category: 'success',
      retry: 'none'
    });
    if (!linked) {
      logFinish('failed-reload', attempt);
      if (attempt) authAttemptOwner.settle(attempt);
      syncPending();
      // A failed link did not add the camera. An older note must not resume
      // that camera after this reload. ENG-4649.
      clearOnboardingResume();
      if (isElectronApp()) ElectronAPI.onReloadStudio();
      window.location.reload();
      return;
    }
    // Remove only this linked camera's persisted placeholder before reload; keep raw ownership
    // until cleanup settles so another submit cannot race the same camera.
    // Bounded: each removal settles within PENDING_CAMERA_REMOVE_TIMEOUT_MS.
    const linkedSource = attemptSourceRef.current;
    let stillCurrent = false;
    try {
      await removeIrisTransitoryCamerasAt(linkedSource.address, () => getAccessTokenSilently());
    } catch {
      // Linking succeeded independently of this best-effort placeholder cleanup.
      logger.warning('Unable to clear pending camera records after successful link.');
    } finally {
      stillCurrent = canFinishLinked(attempt);
      logFinish(stillCurrent ? 'linked-reload' : 'linked-left-during-cleanup', attempt);
      if (attempt) authAttemptOwner.settle(attempt);
      syncPending();
    }
    if (!stillCurrent) return;
    setShowVideo(false);
    setShow(false);
    // ENG-4649: the reload destroys the first-run wizard's progress, because
    // everything it holds about the run is state in this renderer. Record the
    // agent we just linked so the wizard can resume, and only while the wizard
    // owns the permissions ask.
    noteEmbeddedLinkForOnboarding(
      linkedAgentIdRef.current ?? '',
      useOnboardingPermissionsClaim.getState().funnelOwnsPermissions,
      {
        ...(linkedSource.name ? { name: linkedSource.name } : {}),
        ...(linkedSource.address ? { host: linkedSource.address } : {})
      }
    );
    if (isElectronApp()) ElectronAPI.onReloadStudio();
    window.location.reload();
  };

  const handlePresentationTimeout = useLatestFn((timeoutError: DirectLinkTimeoutError) => {
    const attempt = attemptRef.current;
    if (!attempt || !authAttemptOwner.markPresentationTimeout(attempt)) return;
    const operation: DirectLinkAuthOperation =
      timeoutError.leg === 'cloud link to agent'
        ? 'cloud-link-agent'
        : timeoutError.leg === 'access token'
          ? 'auth-access-token'
          : 'camera-link-details';
    const outcome = classifyDirectLinkAuthOutcome({
      operation,
      isTimeout: true,
      timeoutMs: timeoutError.timeoutMs,
      attemptReference: attempt.id
    });
    setConnecting(null);
    setError(outcome.message);
    syncPending();
    reportAuth(operation, outcome.reasonId);
    logDirectLinkLegFailure(
      operation === 'cloud-link-agent' || operation === 'auth-access-token'
        ? 'cloud-link-agent'
        : 'cloud-link-registration',
      timeoutError.message
    );
  });

  const { requestDirectLinkToAgentDetails } = useConnectToMDNSService({
    onTimeout: handlePresentationTimeout
  });

  const { linkBridgeDirect } = useDirectLinkAgent(
    (linked) => finishAttempt(true, linked),
    (linkError, operation = 'cloud-link-agent') => {
      const attempt = attemptRef.current;
      const canApply = canApplyAttempt(attempt);
      if (attempt) authAttemptOwner.settle(attempt);
      syncPending();
      if (!canApply) return;
      const outcome = classifyDirectLinkAuthOutcome({
        operation,
        code: directLinkErrorCode(linkError),
        message: linkError?.message,
        signInRequired: auth0SignInRequired(linkError),
        attemptReference: attempt?.id ?? 'auth-unknown'
      });
      reportAuth(outcome.operation, outcome.reasonId, directLinkErrorCode(linkError));
      logDirectLinkLegFailure(
        'cloud-link-agent',
        linkError?.message ?? 'Unknown direct link failure',
        directLinkErrorCode(linkError)
      );
      logger.warning(`Direct bridge link failed: ${outcome.message}`);
      setError(outcome.message);
      setConnecting(null);
    },
    { onTimeout: handlePresentationTimeout }
  );

  const { track } = useAnalytics();
  const deviceLifecycleTracker = createDeviceLifecycleTracker(track);
  const authAnalyticsProps = {
    studio_id: roomId,
    // This dialog is entered from a selected discovery, never inferred from its protocol.
    [ONBOARDING_PROPERTIES.ADD_METHOD]: ONBOARDING_ADD_METHOD.DISCOVERY,
    ...(sourceSelection?.video?.protocol ? { protocol: sourceSelection.video.protocol } : {}),
    ...(sourceSelection?.modelId
      ? resolveDeviceAnalyticsCatalogContext(sourceSelection.modelId)
      : {}),
    ...resolveEmbeddedFlowProps(isEmbeddedDiscovery(sourceSelection))
  };

  useEffect(() => {
    if (lastIdentityKey.current === identityKey) return;
    lastIdentityKey.current = identityKey;
    // Every identity transition advances the generation, including away-and-back flow changes.
    diagnosticRef.current?.report({ operation: 'unknown', category: 'superseded', retry: 'none' });
    authAttemptOwner.supersede();
    setError(null);
    setConnecting(null);
    setLockoutStatus(null);
    setRawPending(authAttemptOwner.rawPending());
  }, [identityKey, setError, setConnecting, setLockoutStatus, setRawPending]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      const attempt = attemptRef.current;
      // The modal can unmount while the "Unlink Bridge" prompt is still parked (panel
      // key change, route change, Add dialog closed by state). The prompt is torn down
      // without routing to onCancel, so nothing else settles this attempt. No cloud
      // write runs until the operator answers, so settling here cannot authorize a
      // second write — it releases the latch that would otherwise disable Authenticate
      // for every camera this session.
      // Per settle-every-terminal-branch (ai.knowledge/global): settle the latch on unmount.
      if (attempt && awaitingUnlinkDecisionRef.current && !attempt.settled) {
        authAttemptOwner.settle(attempt);
        awaitingUnlinkDecisionRef.current = false;
        setRawPending(authAttemptOwner.rawPending());
      }
      // Release this modal's presentation, but retain the raw-flight guard until IO settles.
      // A different modal may already own a newer attempt; its presentation belongs to it.
      if (authAttemptOwner.current() !== attemptRef.current) return;
      diagnosticRef.current?.report({ operation: 'unknown', category: 'cancelled', retry: 'none' });
      authAttemptOwner.supersede();
      setError(null);
      setConnecting(null);
      setLockoutStatus(null);
      setRawPending(authAttemptOwner.rawPending());
    };
  }, [setError, setConnecting, setLockoutStatus, setRawPending]);

  useEffect(() => {
    if (!show) return;
    if (!hasDirectLinkIpcInvoke()) {
      const outcome = classifyDirectLinkAuthOutcome({
        operation: 'camera-link-details',
        hasDirectLinkIpc: false,
        attemptReference: 'auth-ipc'
      });
      const diagnostic = createAddFlowDiagnostic({
        actor: 'client',
        sourceId: sourceSelection?.id?.toString(),
        agentId: storeAgentId,
        roomId
      });
      diagnostic.report({ operation: outcome.operation, category: 'unsupported', retry: 'none' });
      setError(outcome.message);
      setConnecting(null);
    }
  }, [show, setError, setConnecting]);

  const presentCameraFailure = (
    attempt: DirectLinkAttempt,
    operation: DirectLinkAuthOperation,
    protoError: unknown
  ) => {
    const protoCode = directLinkErrorCode(protoError);
    const message =
      protoError && typeof protoError === 'object' && 'message' in protoError
        ? String((protoError as { message: unknown }).message ?? '')
        : '';
    const outcome = classifyDirectLinkAuthOutcome({
      operation,
      code: protoCode,
      message,
      attemptReference: attempt.id
    });
    authAttemptOwner.settle(attempt);
    setConnecting(null);
    setError(outcome.message);
    syncPending();
    reportAuth(outcome.operation, outcome.reasonId, protoCode);
    logDirectLinkLegFailure('camera-credential-auth', message, protoCode);
    // App-log copy for field diagnosis; HIL still reads the per-leg console line.
    // Classified copy only — never raw protobuf text.
    // Per rule: use-canonical-logger-not-console (ai.knowledge/platform/studio.client).
    logger.warning(`Device credential check failed: ${outcome.message}`);
    // Safe allowlisted analytics only — never arbitrary protobuf text.
    track(EVENT_NAMES.DEVICE_AUTH_INVALID, EVENT_CATEGORIES.DEVICE, {
      reasonId: outcome.reasonId,
      operation: outcome.operation,
      code: safeAddFlowStatus(protoCode),
      attemptReference: attempt.id,
      deviceType: 'onvif',
      ...authAnalyticsProps
    });
  };

  const handleConfirm = async (credentials: M_Credentials) => {
    if (!hasDirectLinkIpcInvoke()) {
      const outcome = classifyDirectLinkAuthOutcome({
        operation: 'camera-link-details',
        hasDirectLinkIpc: false,
        attemptReference: 'auth-ipc'
      });
      setError(outcome.message);
      setConnecting(null);
      return;
    }

    const attempt = authAttemptOwner.begin(identityRef.current);
    if (!attempt) {
      syncPending();
      return;
    }

    attemptRef.current = attempt;
    attemptSourceRef.current = {
      ...(sourceSelection?.name ? { name: sourceSelection.name } : {}),
      ...(sourceSelection?.address ? { address: sourceSelection.address } : {})
    };
    // A fresh attempt is not awaiting the unlink prompt until it reaches that branch.
    awaitingUnlinkDecisionRef.current = false;
    diagnosticRef.current = createAddFlowDiagnostic({
      reference: attempt.id,
      actor: 'client',
      sourceId: sourceSelection?.id?.toString(),
      agentId: storeAgentId,
      roomId
    });
    setError(null);
    setLockoutStatus(null);
    setConnecting('Connecting to device...');
    syncPending();

    track(EVENT_NAMES.ENTER_ONVIF_PASSWORD, EVENT_CATEGORIES.VIDEO_SOURCES, authAnalyticsProps);

    try {
      const { linkDetails, statusDetails } = (await requestDirectLinkToAgentDetails({
        service: sourceSelection,
        username: credentials.username!,
        password: credentials.password!
      })) as DirectLinkRequestDetails;

      if (!canApplyAttempt(attempt)) {
        authAttemptOwner.settle(attempt);
        syncPending();
        return;
      }

      if (linkDetails?.lockoutStatus?.isLocked) {
        authAttemptOwner.settle(attempt);
        setConnecting(null);
        reportAuth('camera-auth', 'R2');
        setLockoutStatus(linkDetails.lockoutStatus);
        syncPending();
        return;
      }

      if (hasProtoError(linkDetails?.error)) {
        presentCameraFailure(attempt, 'camera-link-details', linkDetails?.error);
        return;
      }

      if (hasProtoError(statusDetails?.error)) {
        presentCameraFailure(attempt, 'camera-status', statusDetails?.error);
        return;
      }

      if (typeof statusDetails?.linked !== 'boolean') {
        const outcome = classifyDirectLinkAuthOutcome({
          operation: 'camera-status',
          attemptReference: attempt.id
        });
        authAttemptOwner.settle(attempt);
        setConnecting(null);
        setError(outcome.message || DIRECT_LINK_R4_GENERIC_MESSAGE);
        syncPending();
        reportAuth('camera-status', outcome.reasonId);
        logDirectLinkLegFailure('camera-credential-auth', 'missing status');
        return;
      }

      // Status alone cannot authorize a cloud write. Malformed link data is a
      // current terminal failure, not a superseded continuation to ignore.
      if (!linkDetails?.bridgeId || !linkDetails.requestId) {
        presentCameraFailure(attempt, 'camera-link-details', undefined);
        return;
      }

      // Report camera authentication once, after validating this current attempt.
      // Optional telemetry must not prevent the authenticated camera from linking.
      try {
        // ENG-4620 (main #1867): carry the camera's new agent id so the
        // claim-to-stream funnel joins this row to the device grain.
        track(EVENT_NAMES.DEVICE_AUTHENTICATE, EVENT_CATEGORIES.DEVICE, {
          deviceType: 'iris-enabled',
          ...authAnalyticsProps,
          agent_id: linkDetails.bridgeId
        });
      } catch {
        // Linking remains available when the analytics transport is unavailable.
      }

      const startCloudLink = () => {
        if (!canApplyAttempt(attempt)) {
          authAttemptOwner.settle(attempt);
          syncPending();
          return;
        }
        setError(null);
        setConnecting('Connecting to device...');
        linkedAgentIdRef.current = linkDetails.bridgeId;
        linkBridgeDirect({
          agentId: linkDetails.bridgeId,
          requestId: linkDetails.requestId,
          token: linkDetails.token,
          shouldContinue: () => canApplyAttempt(attempt)
        });
      };

      // linked === true is the only proof of an already-linked device. An
      // allocated CurrentStatus with linked === false (current firmware) and
      // the old-firmware /status fallback both proceed without inventing a link.
      if (statusDetails.linked === true) {
        // The prompt is now parked awaiting the operator; no cloud write starts until
        // they answer, so an unmount here must settle the attempt (see the unmount
        // cleanup). Per settle-every-terminal-branch (ai.knowledge/global).
        awaitingUnlinkDecisionRef.current = true;
        prompt.confirm({
          title: 'Unlink Bridge',
          message: [
            'This device is currently linked to another Studio.',
            'By continuing, it will be unlinked from the existing Studio and linked with this one.'
          ].join('\n'),
          okay: {
            variant: 'danger',
            label: 'Link Bridge with this Studio'
          },
          cancel: {
            label: 'Cancel'
          },
          onConfirm: async (answer) => {
            // Answered: the terminal paths below settle the attempt, so the unmount
            // cleanup must no longer treat it as parked.
            awaitingUnlinkDecisionRef.current = false;
            if (answer) {
              startCloudLink();
              return;
            }
            const canApply = canApplyAttempt(attempt);
            authAttemptOwner.settle(attempt);
            if (canApply) {
              diagnosticRef.current?.report({
                operation: 'cloud-link-agent',
                category: 'cancelled',
                retry: 'none'
              });
              setConnecting(null);
            }
            syncPending();
          }
        });
      } else {
        startCloudLink();
      }
    } catch (error_) {
      const message = error_ instanceof Error ? error_.message : 'An unknown error occurred';
      const outcome = classifyDirectLinkAuthOutcome({
        operation: error_ instanceof DirectLinkFailure ? error_.operation : 'camera-link-details',
        code: directLinkErrorCode(error_),
        message,
        attemptReference: attempt.id
      });
      logDirectLinkLegFailure('cloud-link-registration', message, directLinkErrorCode(error_));
      logger.warning(`Direct link request failed: ${outcome.message}`);
      const canApply = canApplyAttempt(attempt);
      authAttemptOwner.settle(attempt);
      syncPending();
      if (!canApply) return;
      setConnecting(null);
      setError(outcome.message);
      reportAuth(outcome.operation, outcome.reasonId, directLinkErrorCode(error_));
      if (outcome.reasonId === 'R3' && sourceSelection?.id) {
        deviceLifecycleTracker.trackDeviceAddFailed({
          ...authAnalyticsProps,
          device_id: sourceSelection.id.toString(),
          attemptReference: attempt.id,
          operation: outcome.operation,
          error_type: DIRECT_LINK_TRANSPORT_ERROR_TYPE,
          error_message: DIRECT_LINK_UNREACHABLE_EVENT_MESSAGE
        });
      }
    }
  };

  const releaseUi = () => {
    releasedAttemptRef.current = attemptRef.current;
    diagnosticRef.current?.report({ operation: 'unknown', category: 'cancelled', retry: 'none' });
    authAttemptOwner.supersede();
    setShow(false);
    setError(null);
    setLockoutStatus(null);
    setConnecting(null);
    syncPending();
  };

  return show ? (
    <AddSourceAuthWrapper
      open={show}
      error={error}
      connecting={connecting}
      rawPending={rawPending}
      lockoutStatus={lockoutStatus}
      onOpenChange={(open) => {
        if (!open) releaseUi();
        else setShow(true);
      }}
      onDismiss={releaseUi}
      onConfirm={handleConfirm}
      onLockoutChange={setLockoutStatus}
    />
  ) : null;
};
